import { useEffect, useMemo, useState } from 'react'
import {
  Alert, App, Button, Card, Col, DatePicker, Descriptions, Form, Input,
  InputNumber, Row, Select, Space, Table, Tag, Typography,
} from 'antd'
import { useMutation, useQuery } from '@tanstack/react-query'
import dayjs from 'dayjs'

import apiClient from '@/shared/api/client'
import {
  spareProcurementApi,
  type InvoiceMatchStatus,
  type SpareInvoiceLineInput,
  type SpareInvoiceResult,
} from '@/features/maintenance/spareProcurementApi'
import PageHeader from '@/shared/components/PageHeader'
import { usePermission } from '@/shared/hooks/usePermission'
import { usePlantId } from '@/shared/hooks/usePlantId'
import ProcurementStatusBadge from './components/ProcurementStatusBadge'

const { Text } = Typography

interface POLineRow {
  id: string
  spare_id: string | null
  quantity: number | null
  quantity_kg: number | null
  uom: string | null
  rate: number | null
  rate_per_kg: number | null
}

interface PORow {
  id: string
  po_no: string
  supplier_id: string | null
  supplier_name?: string
  status: string
  lines: POLineRow[]
}

interface GRNLineRow {
  id: string
  grn_id: string
  po_line_id: string | null
  spare_id: string | null
  quantity_accepted: number
  uom: string
  rate_per_unit: number
  landed_rate_per_unit: number | null
}

interface InvoiceLineDraft {
  grn_line_id: string
  invoice_qty: number
  invoice_rate: number
  invoice_gst_pct: number
}

/**
 * Phase 3.2-B — Spare Invoice 3-Way Match.
 *
 * Spec §6. Accounts officer picks PO → GRN → enters invoice line qty/rate.
 * Variance is computed by backend. If MATCHED → posted. If VARIANCE →
 * BLOCKED unless an authorised override is supplied.
 */
export default function SpareInvoiceMatchPage() {
  const { message } = App.useApp()
  const { plantId } = usePlantId()
  const [poId, setPoId] = useState<string | undefined>()
  const [grnId, setGrnId] = useState<string | undefined>()
  const [invoiceNo, setInvoiceNo] = useState('')
  const [invoiceDate, setInvoiceDate] = useState<dayjs.Dayjs>(dayjs())
  const [lines, setLines] = useState<Record<string, InvoiceLineDraft>>({})
  const [qtyTol, setQtyTol] = useState<number>(0)
  const [rateTol, setRateTol] = useState<number>(0)
  const [overrideReason, setOverrideReason] = useState('')
  const [applyOverride, setApplyOverride] = useState(false)
  const [result, setResult] = useState<SpareInvoiceResult | null>(null)

  const canOverride = usePermission('accounting.spare_invoice')

  // POs
  const { data: pos } = useQuery<PORow[]>({
    queryKey: ['pos-for-invoice', plantId],
    enabled: !!plantId,
    queryFn: () => apiClient.get('/purchase-orders', {
      params: { plant_id: plantId },
    }).then((r) => r.data),
  })
  // PO record (we only use it for completeness; the GRN list is the
  // authoritative source of received lines).
  void (pos ?? []).find((p) => p.id === poId)

  // GRN lines for the picked PO (filter to lines belonging to this PO)
  // Done by fetching procurement links + matching GRN ids — we use the
  // existing `listLinks` to discover open GRNs for the PO.
  const { data: links } = useQuery({
    queryKey: ['sp-links-for-po', plantId, poId],
    enabled: !!plantId && !!poId,
    queryFn: () => spareProcurementApi.listLinks({
      plant_id: plantId, limit: 200,
    }),
  })
  const grnsForPO = useMemo(() => {
    return Array.from(new Set(
      (links ?? [])
        .filter((l) => l.po_id === poId && l.grn_id != null)
        .map((l) => l.grn_id as string),
    ))
  }, [links, poId])

  // GRN line list — pulled via the dashboard endpoint then filtered.
  // We re-use the enriched-link table: every link with grn_line_id +
  // matching po_id is a candidate row.
  const grnLinesForPO: GRNLineRow[] = useMemo(() => {
    if (!poId || !grnId || !links) return []
    return (links ?? [])
      .filter((l) => l.po_id === poId && l.grn_id === grnId && l.grn_line_id)
      .map((l) => ({
        id: l.grn_line_id!,
        grn_id: l.grn_id!,
        po_line_id: null,
        spare_id: l.spare_id,
        quantity_accepted: l.grn_qty_accepted ?? 0,
        uom: 'NOS',
        rate_per_unit: l.grn_landed_rate ?? 0,
        landed_rate_per_unit: l.grn_landed_rate ?? null,
      }))
  }, [links, poId, grnId])

  // Reset state when PO/GRN changes
  useEffect(() => { setLines({}); setResult(null) }, [poId, grnId])

  const setLine = (id: string, patch: Partial<InvoiceLineDraft>) => {
    setLines((s) => {
      const defaults: InvoiceLineDraft = {
        grn_line_id: id, invoice_qty: 0, invoice_rate: 0, invoice_gst_pct: 18,
      }
      return {
        ...s,
        [id]: { ...defaults, ...(s[id] ?? {}), ...patch },
      }
    })
  }

  const submit = useMutation({
    mutationFn: () => {
      if (!poId || !grnId) throw new Error('Pick PO and GRN first')
      const lineList: SpareInvoiceLineInput[] = Object.values(lines)
        .filter((l) => l.invoice_qty > 0)
      if (lineList.length === 0) throw new Error('Enter at least one invoice line')
      return spareProcurementApi.bookSpareInvoice({
        po_id: poId, grn_id: grnId,
        invoice_no: invoiceNo,
        invoice_date: invoiceDate.format('YYYY-MM-DD'),
        lines: lineList,
        qty_tol_pct: qtyTol,
        rate_tol_pct: rateTol,
        override_reason: overrideReason || undefined,
      }, applyOverride)
    },
    onSuccess: (r) => {
      setResult(r)
      if (r.match_status === 'POSTED') {
        message.success('Invoice posted successfully')
      } else if (r.match_status === 'BLOCKED') {
        message.warning('Variance exceeds tolerance — booking blocked.')
      }
    },
    onError: (e) => message.error((e as { message?: string })?.message ?? 'Failed'),
  })

  const totalTaxable = Object.values(lines).reduce(
    (a, l) => a + (l.invoice_qty * l.invoice_rate), 0,
  )
  const totalGst = Object.values(lines).reduce(
    (a, l) => a + (l.invoice_qty * l.invoice_rate * (l.invoice_gst_pct / 100)), 0,
  )

  return (
    <div>
      <PageHeader
        title="Spare Invoice — 3-Way Match"
        subtitle="PO ↔ GRN ↔ Invoice. Variance > tolerance → BLOCKED. Override needs reason."
      />

      <Row gutter={12}>
        <Col xs={24} lg={16}>
          <Card size="small" style={{ marginBottom: 12 }}>
            <Space wrap>
              <Select
                showSearch
                placeholder="PO"
                style={{ width: 280 }}
                value={poId}
                onChange={setPoId}
                options={(pos ?? []).map((p) => ({
                  label: `${p.po_no} — ${p.supplier_name ?? '...'}`,
                  value: p.id,
                }))}
                filterOption={(i, o) =>
                  (o?.label as string).toLowerCase().includes(i.toLowerCase())
                }
              />
              <Select
                showSearch
                placeholder="GRN"
                style={{ width: 260 }}
                value={grnId}
                onChange={setGrnId}
                disabled={!poId || grnsForPO.length === 0}
                options={grnsForPO.map((g) => ({ label: g.slice(0, 8), value: g }))}
              />
              <Input
                placeholder="Invoice No"
                style={{ width: 200 }}
                value={invoiceNo}
                onChange={(e) => setInvoiceNo(e.target.value)}
              />
              <DatePicker
                value={invoiceDate}
                onChange={(v) => v && setInvoiceDate(v)}
              />
            </Space>
          </Card>

          {grnLinesForPO.length > 0 && (
            <Card title="Invoice line entry" size="small"
              style={{ marginBottom: 12 }}>
              <Table
                rowKey="id"
                pagination={false}
                size="small"
                dataSource={grnLinesForPO}
                columns={[
                  {
                    title: 'GRN line', dataIndex: 'id', key: 'id', width: 110,
                    render: (v: string) => <Text code>{v.slice(0, 8)}</Text>,
                  },
                  {
                    title: 'GRN qty (accepted)', dataIndex: 'quantity_accepted',
                    key: 'qa', width: 130,
                    render: (v: number) => v.toFixed(2),
                  },
                  {
                    title: 'PO rate (landed)', key: 'pr', width: 130,
                    render: (_: unknown, l: GRNLineRow) => `₹ ${l.rate_per_unit.toFixed(4)}`,
                  },
                  {
                    title: 'Invoice qty *', key: 'iq', width: 130,
                    render: (_: unknown, l: GRNLineRow) => (
                      <InputNumber min={0} size="small" style={{ width: 100 }}
                        value={lines[l.id]?.invoice_qty ?? 0}
                        onChange={(v) => setLine(l.id, { invoice_qty: Number(v) || 0 })}
                      />
                    ),
                  },
                  {
                    title: 'Invoice rate *', key: 'ir', width: 140,
                    render: (_: unknown, l: GRNLineRow) => (
                      <InputNumber min={0} size="small" style={{ width: 110 }}
                        value={lines[l.id]?.invoice_rate ?? 0}
                        onChange={(v) => setLine(l.id, { invoice_rate: Number(v) || 0 })}
                      />
                    ),
                  },
                  {
                    title: 'GST %', key: 'gst', width: 90,
                    render: (_: unknown, l: GRNLineRow) => (
                      <InputNumber min={0} max={28} size="small"
                        style={{ width: 80 }}
                        value={lines[l.id]?.invoice_gst_pct ?? 18}
                        onChange={(v) => setLine(l.id, { invoice_gst_pct: Number(v) || 0 })}
                      />
                    ),
                  },
                ]}
              />

              <Row gutter={12} style={{ marginTop: 12 }}>
                <Col xs={12}>
                  <Form.Item label="Qty tolerance %">
                    <InputNumber
                      min={0} max={20} value={qtyTol} style={{ width: '100%' }}
                      onChange={(v) => setQtyTol(Number(v) || 0)}
                    />
                  </Form.Item>
                </Col>
                <Col xs={12}>
                  <Form.Item label="Rate tolerance %">
                    <InputNumber
                      min={0} max={20} value={rateTol} style={{ width: '100%' }}
                      onChange={(v) => setRateTol(Number(v) || 0)}
                    />
                  </Form.Item>
                </Col>
              </Row>

              {result?.match_status === 'BLOCKED' && (
                <Alert
                  type="error"
                  showIcon
                  style={{ marginBottom: 12 }}
                  message="Variance over tolerance — booking blocked."
                  description={(
                    <>
                      <div>Max qty variance: {result.variance_summary?.qty_variance_pct_max.toFixed(2)}%</div>
                      <div>Max rate variance: {result.variance_summary?.rate_variance_pct_max.toFixed(2)}%</div>
                      {canOverride && (
                        <div style={{ marginTop: 8 }}>
                          <Text>Authorised override — reason (≥5 chars):</Text>
                          <Input.TextArea
                            rows={2}
                            value={overrideReason}
                            onChange={(e) => setOverrideReason(e.target.value)}
                          />
                          <Space style={{ marginTop: 6 }}>
                            <Button
                              danger
                              onClick={() => {
                                setApplyOverride(true)
                                submit.mutate()
                              }}
                              disabled={!overrideReason || overrideReason.length < 5}>
                              Re-submit with override
                            </Button>
                          </Space>
                        </div>
                      )}
                    </>
                  )}
                />
              )}

              <Button
                type="primary"
                onClick={() => { setApplyOverride(false); submit.mutate() }}
                loading={submit.isPending}
                disabled={!invoiceNo}>
                Match + Post
              </Button>
            </Card>
          )}
        </Col>

        <Col xs={24} lg={8}>
          <Card title="Computed (read-only)" size="small">
            <Descriptions size="small" bordered column={1}>
              <Descriptions.Item label="Taxable total">
                ₹ {totalTaxable.toFixed(2)} <Tag>FRONTEND PREVIEW</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="GST total">
                ₹ {totalGst.toFixed(2)}
              </Descriptions.Item>
              <Descriptions.Item label="Grand total">
                <Text strong>₹ {(totalTaxable + totalGst).toFixed(2)}</Text>
              </Descriptions.Item>
            </Descriptions>

            {result && (
              <Descriptions
                size="small" bordered column={1}
                style={{ marginTop: 12 }}>
                <Descriptions.Item label="Match status">
                  <ProcurementStatusBadge status={result.match_status as InvoiceMatchStatus} />
                </Descriptions.Item>
                <Descriptions.Item label="Backend total">
                  ₹ {result.total_payable.toFixed(2)} <Tag>SYSTEM CALCULATED</Tag>
                </Descriptions.Item>
                {result.voucher_id && (
                  <Descriptions.Item label="Voucher posted">
                    <Text code>{result.voucher_id.slice(0, 8)}</Text>
                  </Descriptions.Item>
                )}
              </Descriptions>
            )}
            <Alert
              style={{ marginTop: 12 }}
              showIcon
              type="info"
              message="Variance, advance adjustment and balance payable are computed by the backend."
            />
          </Card>
        </Col>
      </Row>
    </div>
  )
}
